"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Menu, X, Sun, Moon, LogOut } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/app/context/AuthContext";

export default function Header() {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const { theme, setTheme } = useTheme();
    const [mounted, setMounted] = useState(false);
    const { isLoggedIn, userEmail, logout } = useAuth();

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <header className="w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-md transition-all duration-300">
            <div className="container mx-auto flex items-center justify-between py-4 px-6">

                {/* Linksbündiger Bereich für das Logo */}
                <div className="flex-1">
                    <Link
                        href="/"
                        className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-purple-400"
                    >
                        AnnoModels
                    </Link>
                </div>

                {/* Mittig zentrierte Navigation */}
                <nav className="hidden md:flex items-center justify-center gap-10 flex-1">
                    {[
                        { href: "/annotation", label: "Annotationen" },
                        { href: "/process", label: "Prozessmodelle" },
                        { href: "/pubserv", label: "öffentliche Dienstleistungen" },
                        { href: "/pubservtest", label: "öffentliche Dienstleistungen 2" }
                    ].map((item) => (
                        <Link
                            key={item.href}
                            href={item.href}
                            className="text-lg font-medium hover:text-blue-600 dark:hover:text-blue-400 transition duration-200"
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                {/* Rechtsbündiger Bereich für Aktionen */}
                <div className="flex-1 flex justify-end items-center space-x-4">
                    {/* Dark Mode Toggle */}
                    {mounted && (
                        <button
                            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:scale-110 transition-transform duration-200"
                            title="Dark Mode umschalten"
                            aria-label="Dark Mode umschalten"
                        >
                            {theme === "dark" ? (
                                <Sun size={24} className="text-yellow-400" />
                            ) : (
                                <Moon size={24} className="text-gray-900" />
                            )}
                        </button>
                    )}

                    {/* User Info */}
                    {isLoggedIn && userEmail && (
                        <>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {userEmail}
                            </span>
                            <button
                                onClick={logout}
                                className="flex items-center gap-2 rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                aria-label="Abmelden"
                                title="Abmelden"
                            >
                                <LogOut size={18} />
                                <span>Abmelden</span>
                            </button>
                        </>
                    )}

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMenuOpen(!isMenuOpen)}
                        className="md:hidden p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:scale-110 transition-transform duration-200"
                    >
                        {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            <nav
                className={`fixed inset-0 bg-white dark:bg-gray-900 flex flex-col items-center justify-center transform ${
                    isMenuOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
                } transition-all duration-300 md:hidden`}
            >
                {/* Close Button */}
                <button
                    onClick={() => setMenuOpen(false)}
                    className="absolute top-6 right-6 p-3 bg-gray-200 dark:bg-gray-700 rounded-full hover:scale-110 transition-transform"
                >
                    <X size={28} />
                </button>

                {/* Links */}
                {[
                    { href: "/annotation", label: "Annotationen" },
                    { href: "/process", label: "Prozessmodelle" },
                    { href: "/pubserv", label: "öffentliche Dienstleistungen" },
                    { href: "/pubservtest", label: "öffentliche Dienstleistungen 2" }
                ].map((item) => (
                    <Link
                        key={item.href}
                        href={item.href}
                        className="text-2xl font-semibold text-gray-900 dark:text-white py-4 hover:text-blue-600 dark:hover:text-blue-400 transition duration-200"
                        onClick={() => setMenuOpen(false)}
                    >
                        {item.label}
                    </Link>
                ))}
            </nav>
        </header>
    );
}